// File upload constants and utilities
export const FILE_UPLOAD_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB in bytes
  MAX_FILES_PER_UPLOAD: 5, // Max 5 files per upload
  ALLOWED_FILE_TYPES: [
    'image/jpeg',
    'image/png', 
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ],
  ALLOWED_EXTENSIONS: ['.pdf', '.doc', '.docx', '.txt', '.jpg', '.jpeg', '.png', '.gif', '.xlsx', '.xls']
};

export interface FileUploadError {
  type: 'size' | 'type' | 'count' | 'unknown';
  message: string;
  fileName?: string;
}

export const validateFile = (file: File): FileUploadError | null => {
  // Check file size
  if (file.size > FILE_UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return {
      type: 'size',
      message: `File "${file.name}" is too large. Maximum size is ${FILE_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB`,
      fileName: file.name
    };
  }

  // Check file type
  if (!FILE_UPLOAD_CONFIG.ALLOWED_FILE_TYPES.includes(file.type)) {
    return {
      type: 'type',
      message: `File type "${file.type}" is not allowed`,
      fileName: file.name
    };
  }

  return null;
};

export const validateFileList = (files: File[], currentCount: number = 0): FileUploadError | null => {
  // Check total file count
  if (currentCount + files.length > FILE_UPLOAD_CONFIG.MAX_FILES_PER_UPLOAD) {
    return {
      type: 'count',
      message: `Too many files. Maximum ${FILE_UPLOAD_CONFIG.MAX_FILES_PER_UPLOAD} files allowed`
    };
  }

  // Validate each file
  for (const file of files) {
    const error = validateFile(file);
    if (error) {
      return error;
    }
  }

  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}; 